import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import _ from 'lodash';
import { MessageFilterChanged, SetMessageListSkillFilter } from '../samwin-mail-list/samwin-message-list.actions';
import { MailingFolderEnum } from '../samwin-skills/mailing-folders-enum';
import { MailingSkillTreeItem } from '../samwin-skills/mailing-skill-tree-item';
import { SamwinSkillsState } from '../samwin-skills/samwin-skills.state';
import { SkillConstants } from '../samwin-skills/skill-constants';
import {
   MessageTreeviewEntrySelected,
   MessageTreeviewItemCollapse,
   MessageTreeviewItemCollapseSelectedEntry,
   MessageTreeviewItemExpand,
   MessageTreeviewItemExpandSelectedEntry,
   MessageTreeviewSelectNextEntry,
} from './samwin-mail-skill-browser.actions';

export interface SamwinMessageSkillBrowserStateModel {
   selectedSkill: string;
   expandedItems: string[];
}

@State<SamwinMessageSkillBrowserStateModel>({
   name: 'samwinMessageSkillBrowser',
   defaults: {
      selectedSkill: MailingFolderEnum.INBOX.name,
      expandedItems: [],
   },
})
@Injectable()
export class SamwinMessageSkillBrowserState {
   constructor(private store: Store) {}

   @Selector([SamwinMessageSkillBrowserState])
   public static getExpandedItems(state: SamwinMessageSkillBrowserStateModel): string[] {
      return state.expandedItems;
   }

   @Selector([SamwinMessageSkillBrowserState])
   public static selectedSkillId(state: SamwinMessageSkillBrowserStateModel) {
      return state.selectedSkill;
   }

   @Selector([SamwinSkillsState.getMailingSkillTree, SamwinMessageSkillBrowserState.getExpandedItems])
   public static getMailingTreeviewKeys(skills: MailingSkillTreeItem[], expandedGroups: string[]) {
      const result: string[] = [];
      skills.forEach((skill) => {
         result.push(skill.groupKey);
         if (expandedGroups.includes(skill.groupName)) {
            skill.children.forEach((skillChild) => {
               result.push(skillChild.groupKey);
            });
         }
      });
      // also add TreeView to lose the drop target when moving outside of the treeview
      result.push(SkillConstants.SKILL_TREEVIEW_NAME);
      return result;
   }

   @Selector([SamwinSkillsState.getMailingSkillTree, SamwinMessageSkillBrowserState.getExpandedItems])
   public static getMailingTreeviewItems(skills: MailingSkillTreeItem[], expandedGroups: string[]) {
      const result: MailingSkillTreeItem[] = [];
      skills.forEach((skill) => {
         result.push(skill);
         if (expandedGroups.includes(skill.groupName)) {
            skill.children.forEach((skillChild) => {
               result.push(skillChild);
            });
         }
      });
      return result;
   }

   @Action(MessageTreeviewItemExpand)
   public messageTreeviewItemExpand(ctx: StateContext<SamwinMessageSkillBrowserStateModel>, action: MessageTreeviewItemExpand) {
      const newItems = ctx.getState().expandedItems.concat(action.groupKey);
      ctx.patchState({
         expandedItems: [...new Set(newItems)],
      });
   }

   @Action(MessageTreeviewItemCollapse)
   public MessageTreeviewItemCollapse(ctx: StateContext<SamwinMessageSkillBrowserStateModel>, action: MessageTreeviewItemCollapse) {
      const newItems = ctx.getState().expandedItems.filter((item) => item !== action.groupKey);
      ctx.patchState({
         expandedItems: newItems,
      });
   }

   @Action(MessageTreeviewEntrySelected)
   public skillBrowserSelectEntry(ctx: StateContext<SamwinMessageSkillBrowserStateModel>, action: MessageTreeviewEntrySelected) {
      ctx.patchState({
         selectedSkill: action.groupKey,
      });

      // NOTE: Message Filter gets updated from here. Waitlist could use the Skill-Browser state.
      const key = action.groupKey.split('_');
      const groupName = key[0];
      const groupSkillId = !_.isNaN(+key[1]) ? +key[1] : null;
      this.store.dispatch(new MessageFilterChanged(groupName, groupSkillId));

      // NOTE: Store the selected skill as skill group item in the future?
      // Possibly add function to the waitlist in the future?
      let skillArray: number[] = [];
      if (!(groupName === MailingFolderEnum.INBOX.name && !_.isNil(groupSkillId))) {
         this.store.dispatch(new SetMessageListSkillFilter([]));
         return;
      }
      const skillGroup = this.store.selectSnapshot(SamwinSkillsState.getSkillGroupById)(groupSkillId);
      if (skillGroup.skills.length > 0) {
         skillArray = skillGroup.skills.map((skill) => skill.skillId);
      } else {
         skillArray = [skillGroup.skillId];
      }
      this.store.dispatch(new SetMessageListSkillFilter(skillArray));
   }

   @Action(MessageTreeviewItemExpandSelectedEntry)
   public MessageTreeviewItemExpandSelectedEntry(ctx: StateContext<SamwinMessageSkillBrowserStateModel>, action: MessageTreeviewItemExpandSelectedEntry) {
      const currentValues: MailingSkillTreeItem[] = this.store.selectSnapshot(SamwinMessageSkillBrowserState.getMailingTreeviewItems);
      const skillState = ctx.getState();
      const selectedSkill = _(currentValues).find((value) => value.groupKey === skillState.selectedSkill);
      if (_.isNil(selectedSkill?.skillId)) {
         return ctx.dispatch(new MessageTreeviewItemExpand(selectedSkill.groupKey));
      }
      return;
   }

   @Action(MessageTreeviewItemCollapseSelectedEntry)
   public MessageTreeviewItemCollapseSelectedEntry(ctx: StateContext<SamwinMessageSkillBrowserStateModel>, action: MessageTreeviewItemCollapseSelectedEntry) {
      const currentValues: MailingSkillTreeItem[] = this.store.selectSnapshot(SamwinMessageSkillBrowserState.getMailingTreeviewItems);
      const skillState = ctx.getState();
      const selectedSkill = _(currentValues).find((value) => value.groupKey === skillState.selectedSkill);
      if (_.isNil(selectedSkill?.skillId)) {
         return ctx.dispatch(new MessageTreeviewItemCollapse(selectedSkill.groupKey));
      }
      return ctx.dispatch(new MessageTreeviewEntrySelected(selectedSkill.groupName));
   }

   @Action(MessageTreeviewSelectNextEntry)
   public selectNextEntry(ctx: StateContext<SamwinMessageSkillBrowserStateModel>, action: MessageTreeviewSelectNextEntry) {
      const currentValues: MailingSkillTreeItem[] = this.store.selectSnapshot(SamwinMessageSkillBrowserState.getMailingTreeviewItems);
      const skillState = ctx.getState();
      let currentIndex = _(currentValues).findIndex((value) => value.groupKey === skillState.selectedSkill);
      if (_.isNil(currentIndex)) {
         currentIndex = -1;
      }
      const lastIndex = _(currentValues).findLastIndex();
      let nextIndex = currentIndex + (action.direction === 'up' ? -action.step : action.step);
      if (nextIndex > lastIndex) {
         nextIndex = lastIndex;
      }
      if (nextIndex < 0) {
         nextIndex = 0;
      }
      const selectedSkill = currentValues[nextIndex];
      if (_.isNil(selectedSkill)) {
         return;
      }
      return ctx.dispatch(new MessageTreeviewEntrySelected(selectedSkill.groupKey));
   }
}
